// examples/Sidenav/index.js
import { useEffect, useMemo } from "react";
import { useLocation, NavLink, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Icon from "@mui/material/Icon";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import SidenavCollapse from "examples/Sidenav/SidenavCollapse";
import SidenavRoot from "examples/Sidenav/SidenavRoot";
import sidenavLogoLabel from "examples/Sidenav/styles/sidenav";

import { useMaterialUIController, setMiniSidenav } from "context";
import { useAuth } from "context/AuthContext";

function Sidenav({ color, brand, brandName, routes, ...rest }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentSidenav, whiteSidenav, darkMode } = controller;

  const location = useLocation();
  const theme = useTheme();
  // mini no lg pra baixo (bom compromisso para desktop/tablet)
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  const { logout, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    setMiniSidenav(dispatch, isMobile);
  }, [isMobile, dispatch]);

  useEffect(() => {
    if (isMobile) setMiniSidenav(dispatch, true);
  }, [location.pathname, isMobile, dispatch]);

  let textColor = "white";
  if (transparentSidenav || (whiteSidenav && !darkMode)) textColor = "dark";
  else if (whiteSidenav && darkMode) textColor = "inherit";

  const closeSidenav = () => setMiniSidenav(dispatch, true);

  const handleLogout = () => {
    logout();
    navigate("/authentication/sign-in");
  };

  const isActive = (routePath) =>
    routePath
      ? location.pathname === routePath || location.pathname.startsWith(`${routePath}/`)
      : false;

  const renderRoutes = useMemo(
    () =>
      routes
        .filter(({ visibleFor }) => {
          // Se não tem restrição de visibilidade, mostra para todos
          if (!visibleFor) return true;
          // Se tem restrição, verifica se a permissão do usuário está na lista
          return user && visibleFor.includes(user.permissao);
        })
        .map(({ type, name, icon, title, noCollapse, key, href, route }) => {
          if (type === "title") {
            return (
              <MDTypography
                key={key}
                color={textColor}
                display="block"
                variant="caption"
                fontWeight="bold"
                textTransform="uppercase"
                pl={{ xs: 2, sm: 3 }}
                mt={2}
                mb={1}
                ml={1}
                sx={{
                  fontSize: { xs: "0.65rem", sm: "0.75rem" },
                  letterSpacing: { xs: "0.5px", sm: "0.8px" },
                  opacity: 0.7,
                }}
              >
                {title}
              </MDTypography>
            );
          }

          if (type === "divider") {
            return (
              <Divider
                key={key}
                light={
                  (!darkMode && !whiteSidenav && !transparentSidenav) ||
                  (darkMode && !transparentSidenav && whiteSidenav)
                }
              />
            );
          }

          if (type === "collapse") {
            if (key === "logout") {
              return (
                <MDBox
                  key={key}
                  onClick={handleLogout}
                  role="button"
                  aria-label="Sair da conta"
                  tabIndex={0}
                  onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && handleLogout()}
                  sx={{ cursor: "pointer", outline: "none" }}
                >
                  <SidenavCollapse name={name} icon={icon} />
                </MDBox>
              );
            }

            if (href) {
              return (
                <Link
                  href={href}
                  key={key}
                  target="_blank"
                  rel="noreferrer"
                  sx={{ textDecoration: "none" }}
                >
                  <SidenavCollapse name={name} icon={icon} active={isActive(route)} />
                </Link>
              );
            }

            return (
              <NavLink
                key={key}
                to={route}
                onClick={isMobile ? closeSidenav : undefined}
                style={{ textDecoration: "none" }}
              >
                <SidenavCollapse name={name} icon={icon} active={isActive(route)} />
              </NavLink>
            );
          }

          return null;
        }),
    [
      routes,
      textColor,
      darkMode,
      whiteSidenav,
      transparentSidenav,
      isMobile,
      location.pathname,
      user,
    ]
  );

  return (
    <SidenavRoot
      {...rest}
      variant={isMobile ? "temporary" : "permanent"}
      open={!miniSidenav}
      onClose={closeSidenav}
      ownerState={{ transparentSidenav, whiteSidenav, miniSidenav, darkMode, isMobile }}
      ModalProps={{ keepMounted: true }}
    >
      {/* Header / logo */}
      <MDBox
        pt={{ xs: 2, sm: 3 }}
        pb={{ xs: 1, sm: 1 }}
        px={{ xs: 2, sm: 2 }}
        textAlign="center"
        position="relative"
        display="flex"
        justifyContent="center"
        alignItems="center"
      >
        {isMobile && (
          <MDBox
            position="absolute"
            top={8}
            right={8}
            p={1}
            onClick={closeSidenav}
            sx={{
              cursor: "pointer",
              backgroundColor: "rgba(0,0,0,0.05)",
              borderRadius: "50%",
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.1)",
                transform: "scale(1.1)",
              },
            }}
          >
            <Icon sx={{ fontWeight: "bold", fontSize: 20 }}>close</Icon>
          </MDBox>
        )}

        <MDBox
          component={NavLink}
          to="/"
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={1}
          sx={{ textDecoration: "none", width: "100%" }}
        >
          {brand ? (
            <MDBox
              component="img"
              src={brand}
              alt="Brand"
              sx={{
                width: "auto",
                height: { xs: 40, sm: 48 },
                maxHeight: { xs: 50, sm: 60 },
                borderRadius: "6px",
                objectFit: "contain",
              }}
            />
          ) : null}
          <MDBox width={!brandName && "100%"} sx={(t) => sidenavLogoLabel(t, { miniSidenav })}>
            <MDTypography
              component="h6"
              variant="button"
              fontWeight="medium"
              color={textColor}
              noWrap
              sx={{ fontSize: { xs: "0.875rem", sm: "1rem" } }}
            >
              {brandName}
            </MDTypography>
          </MDBox>
        </MDBox>
      </MDBox>

      <Divider
        light={
          (!darkMode && !whiteSidenav && !transparentSidenav) ||
          (darkMode && !transparentSidenav && whiteSidenav)
        }
      />

      <List
        sx={{
          px: { xs: 1.5, sm: 1.25 },
          py: 1,
          overflowY: "auto",
          height: {
            xs: "calc(100dvh - 120px)",
            sm: "calc(100vh - 120px)",
          },
          paddingBottom: "env(safe-area-inset-bottom, 12px)",
          "&::-webkit-scrollbar": {
            width: "6px",
          },
          "&::-webkit-scrollbar-track": {
            backgroundColor: "transparent",
          },
          "&::-webkit-scrollbar-thumb": {
            backgroundColor: "#C9A635",
            borderRadius: "4px",
          },
          "&::-webkit-scrollbar-thumb:hover": {
            backgroundColor: "rgba(201, 166, 53, 0.8)",
          },
        }}
      >
        {renderRoutes}
      </List>
    </SidenavRoot>
  );
}

Sidenav.defaultProps = { color: "success", brand: "" };

Sidenav.propTypes = {
  color: PropTypes.oneOf(["primary", "secondary", "info", "success", "warning", "error", "dark"]),
  brand: PropTypes.string,
  brandName: PropTypes.string.isRequired,
  routes: PropTypes.arrayOf(PropTypes.object).isRequired,
};

export default Sidenav;
